import { useEffect, useRef, useState } from "react";
import { message } from "antd";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CustomInputField from "../../../../../../CustomHTMLElements/CustomInputField";
import {
  postData,
  postFormDataWithDotNet,
} from "../../../../../../../newApis/dotNetApiMethods";
import * as ajaxEndpoints from "../../../../../../../api/ajax-endpoints";
import { AllPartnershipBusinessSteps } from "../..";
import styles from "../../Partnership.module.scss";
import { ICustomerSubtypeDocument } from "../../../../../../../interfaces/businessProfile";
import { ICorporateCustomer } from "../../../../hooks/useCorporateCustomers";
import { errorHandler } from "../../../../../../../helpers/errorHandler";

const schema = yup.object().shape({});

interface IPartnershipBusinessDocuments {
  handleProceed: (step: number) => void;
  formData: any;
  documentTypes: ICustomerSubtypeDocument;
  resumeCustomerData?: ICorporateCustomer;
}

const PartnershipBusinessDocuments: React.FC<IPartnershipBusinessDocuments> = ({
  handleProceed,
  formData,
  documentTypes,
  resumeCustomerData,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPickingDocument, setIsPickingDocument] = useState(false);
  const [documentFiles, setDocumentFiles] = useState<
    Array<{ code: string; file: File }>
  >([]);

  const formRef = useRef<HTMLFormElement>(null);
  const itemsRef = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    // Initialize refs array based on document types length
    if (documentTypes?.documents) {
      itemsRef.current = itemsRef.current.slice(
        0,
        documentTypes.documents.length
      );
    }
  }, [documentTypes?.documents]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(schema),
  });

  const onChangeFile = async (
    e: React.ChangeEvent<HTMLInputElement>,
    documentCode: string
  ) => {
    if (isPickingDocument) return;

    setIsPickingDocument(true);

    try {
      const file = e.target.files?.[0];
      if (!file) {
        setIsPickingDocument(false);
        return;
      }

      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        message.error("File size must be less than 5MB");
        setIsPickingDocument(false);
        return;
      }

      const allowedTypes = [
        "image/png",
        "image/jpg",
        "image/jpeg",
        "application/pdf",
      ];
      if (!allowedTypes.includes(file.type)) {
        message.error("Only PNG, JPG, JPEG, and PDF files are allowed");
        setIsPickingDocument(false);
        return;
      }

      setValue(documentCode, file.name);
      setDocumentFiles((prev) => {
        // Remove existing file with same code if any
        const filtered = prev.filter((item) => item.code !== documentCode);
        return [...filtered, { code: documentCode, file }];
      });
    } catch (error: any) {
      message.error(errorHandler(error) || "Failed to upload file");
    } finally {
      setIsPickingDocument(false);
    }
  };

  const onSubmit = async (values: any) => {
    setIsSubmitting(true);

    try {
      const form = new FormData();

      documentFiles.forEach((item, index) => {
        form.append(`CustomerDocuments[${index}].Document`, item.file);
        form.append(`CustomerDocuments[${index}].DocumentTypeCode`, item.code);
      });

      form.append(
        "CustomerId",
        formData?.funder_id || resumeCustomerData?.customer_id
      );

      const response = await postFormDataWithDotNet(
        ajaxEndpoints.UPLOAD_CUSTOMER_DOCUMENT,
        form
      );

      // Temporary success message
      message.success(
        response?.data?.message ||
          response?.message ||
          "Business documents uploaded successfully!"
      );

      // Proceed to next step
      handleProceed(AllPartnershipBusinessSteps.COMPLETE_ONBOARDING);
    } catch (error: any) {
      message.error(errorHandler(error) || "Failed to upload documents");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveClick = () => {
    if (formRef.current) {
      formRef.current.requestSubmit();
    }
  };

  const handleNext = () => {
    handleProceed(AllPartnershipBusinessSteps.COMPLETE_ONBOARDING);
  };

  return (
    <div className="mt-5">
      <form
        onSubmit={handleSubmit(onSubmit)}
        ref={formRef}
        className={styles.businessForm}
      >
        {documentTypes?.documents?.map((docType, index) => (
          <div key={docType.documentId}>
            <CustomInputField
              type="text"
              label={`${docType.documentName} *`}
              name={docType.documentTypeCode}
              placeholder=""
              reference={register}
              errors={errors[docType.documentTypeCode]}
              readOnly
            />
            <button
              type="button"
              className="btn btn-sm advancly-btn mb-3"
              onClick={() => itemsRef.current[index]?.click()}
            >
              {documentFiles.find((f) => f.code === docType.documentTypeCode)
                ? "Replace"
                : "Choose File"}
            </button>
            <input
              type="file"
              className="d-none"
              ref={(el) => (itemsRef.current[index] = el)}
              onChange={(e) => onChangeFile(e, docType.documentTypeCode)}
              accept="image/png, image/jpg, image/jpeg, application/pdf"
            />
          </div>
        ))}

        <div className={styles.ActionButtonContainer}>
          <button
            type="button"
            onClick={handleSaveClick}
            className="btn advancly-btn btn-lg"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Uploading..." : "Save"}
          </button>
          <button
            type="button"
            onClick={handleNext}
            className="btn advancly-btn btn-lg"
          >
            Next
          </button>
        </div>
      </form>
    </div>
  );
};

export default PartnershipBusinessDocuments;
